import { useUserContext } from "@contexts/UserContext/index.context";
import {
  Close as CloseIcon,
  Email,
  Info,
  NotificationImportant,
  NotificationsActive,
  Sms,
} from "@mui/icons-material";
import { Avatar, Box, Card, Chip, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { getAdminNotificationPath, type NotificationMetadata } from "@repo/types";
import { useQueryClient } from "@tanstack/react-query";
import { UserRole } from "@types_def/models/user.type";
import { AnimatePresence, motion } from "framer-motion";
import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { translateNotification } from "@/utils/notificationText";

import { requestPermission, subscribeToMessages } from "../../firebase.config";

type NotifData = {
  title?: string;
  body?: string;
  channel?: string;
  purpose?: string;
  relatedId?: string | number;
  deliveryStatus?: string;
  relatedStatus?: string;
  document?: unknown;
  link?: string;
};

type NotifItem = {
  id: string;
  createdAt: Date;
  read_at: Date | null;
  title: string;
  content: string;
  channel: string;
  data?: NotifData;
  metadata?: NotificationMetadata;
};

const AUTO_DISMISS_TIME = 10000;

function NotificationItem({
  notification,
  onClose,
  onClick,
}: {
  notification: NotifItem;
  onClose: (id: string) => void;
  onClick: (notification: NotifItem) => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const text = translateNotification(t, notification);

  useEffect(() => {
    const timer = setTimeout(() => onClose(notification.id), AUTO_DISMISS_TIME);
    return () => clearTimeout(timer);
  }, [notification.id, onClose]);

  const config = (() => {
    switch (notification.channel) {
      case "EMAIL":
        return { icon: <Email />, color: theme.palette.info.main };
      case "SMS":
        return { icon: <Sms />, color: theme.palette.success.main };
      case "PUSH":
        return { icon: <NotificationImportant />, color: theme.palette.primary.main };
      default:
        return { icon: <NotificationsActive />, color: theme.palette.warning.main };
    }
  })();

  const handleClick = useCallback(() => {
    onClick(notification);
    onClose(notification.id);
  }, [notification, onClick, onClose]);

  const handleCloseClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose(notification.id);
    },
    [notification.id, onClose]
  );

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -50, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8, x: -20, transition: { duration: 0.2 } }}
      style={{ pointerEvents: "auto" }}
    >
      <Card
        elevation={4}
        onClick={handleClick}
        sx={{
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
          width: 360,
          maxWidth: "90vw",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            p: 2,
            pr: 1.5,
            alignItems: "flex-start",
          }}
        >
          <Avatar
            sx={{
              bgcolor: `${config.color}1a`,
              color: config.color,
              width: 40,
              height: 40,
            }}
          >
            {config.icon}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                {text.title}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCloseClick}
                sx={{ ml: 1, mt: -0.5, opacity: 0.7, "&:hover": { opacity: 1 } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                lineHeight: 1.4,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {text.content}
            </Typography>

            {notification?.data?.purpose && (
              <Chip
                label={notification.data.purpose.toLowerCase().replace(/_/g, " ")}
                size="small"
                icon={<Info style={{ fontSize: 12 }} />}
                sx={{
                  mt: 1.5,
                  height: 20,
                  fontSize: "0.65rem",
                  textTransform: "capitalize",
                  bgcolor: "action.hover",
                }}
              />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              bgcolor: config.color,
              width: "100%",
              animation: `shrink ${AUTO_DISMISS_TIME}ms linear forwards`,
              "@keyframes shrink": {
                from: { width: "100%" },
                to: { width: "0%" },
              },
            }}
          />
        </Box>
      </Card>
    </motion.div>
  );
}

function NotificationContainer() {
  const { userState } = useUserContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = userState?.["custom:role"];
  const [notifications, setNotifications] = useState<NotifItem[]>([]);

  // Use ref to track pending driver refreshes
  const pendingDriverRefreshes = useRef<Set<number>>(new Set());
  const refreshTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (role === UserRole.ADMIN) {
      // Surface a visible warning when push setup fails: otherwise the admin
      // silently never receives pushes (empty token, NULL prefs in DB).
      requestPermission().then((status) => {
        if (status !== "failed") return;
        const warning: NotifItem = {
          id: "push-setup-failed",
          createdAt: new Date(),
          read_at: null,
          title: "Notifications push non activées",
          content:
            "L'enregistrement de cet appareil a échoué : vous ne recevrez pas les notifications push. Vérifiez l'autorisation du navigateur puis rechargez la page.",
          channel: "PUSH",
        };
        startTransition(() => {
          setNotifications((prev) =>
            prev.some((n) => n.id === warning.id) ? prev : [warning, ...prev]
          );
        });
      });
    }
  }, [role]);

  // Debounced refresh of driver data (TanStack invalidation)
  const scheduleDriverRefresh = useCallback(
    (driverId: number) => {
      pendingDriverRefreshes.current.add(driverId);

      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }

      refreshTimeout.current = setTimeout(() => {
        if (pendingDriverRefreshes.current.size === 0) return;
        pendingDriverRefreshes.current.clear();

        queryClient.invalidateQueries({ queryKey: ["drivers-table"] });
        queryClient.invalidateQueries({ queryKey: ["getDriverById"] });
        queryClient.invalidateQueries({ queryKey: ["driver-history-table"] });
      }, 500); // Wait 500ms to batch multiple updates
    },
    [queryClient]
  );

  useEffect(() => {
    const unsubscribe = subscribeToMessages((notification) => {
      const newNotif = createNotificationFromFirebase(notification);
      const actor = userState as { id?: number; user?: { id?: number } } | undefined;
      const notifierId = actor?.id ?? actor?.user?.id;

      // Use startTransition for non-urgent UI updates
      startTransition(() => {
        setNotifications((prev) => [newNotif, ...prev]);
      });

      // Batch React Query updates - these are synchronous but optimized
      requestAnimationFrame(() => {
        if (notifierId) {
          queryClient.invalidateQueries({ queryKey: ["notifications", notifierId] });
        }
      });

      // Schedule debounced driver data refresh
      const driverIdFromNotif = Number(notification.data?.relatedId || notification.data?.id);

      if (driverIdFromNotif) {
        scheduleDriverRefresh(driverIdFromNotif);
      }
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
      // Clear pending timeout on unmount
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }
    };
  }, [queryClient, userState, scheduleDriverRefresh]);

  // Background push clicked: the service worker focuses this tab and asks it to route.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const link = event.data?.type === "NOTIFICATION_CLICK" ? event.data.link : undefined;
      if (typeof link === "string" && link.startsWith("/")) navigate(link);
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, [navigate]);

  const handleClose = useCallback((id: string) => {
    startTransition(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    });
  }, []);

  const handleAlertClick = useCallback(
    (notification: NotifItem) => {
      const link = notification.data?.link;
      const path =
        typeof link === "string" && link.startsWith("/")
          ? link
          : getAdminNotificationPath(notification.metadata ?? {});
      if (path) {
        navigate(path);
      }
    },
    [navigate]
  );

  return (
    <Box
      sx={{
        position: "fixed",
        top: 20,
        left: 20,
        zIndex: 9999,
        pointerEvents: "none",
        width: "auto",
        maxWidth: "90vw",
      }}
    >
      <Stack spacing={2} sx={{ width: 400, maxWidth: "100%" }}>
        <AnimatePresence mode="popLayout">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClose={handleClose}
              onClick={handleAlertClick}
            />
          ))}
        </AnimatePresence>
      </Stack>
    </Box>
  );
}

// Helper - keep domain logic separate
function createNotificationFromFirebase(msg: {
  notification?: { title?: string; body?: string };
  data?: Record<string, string>;
}): NotifItem {
  return {
    id: crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    createdAt: new Date(),
    read_at: null,
    title: msg.notification?.title || "",
    content: msg.notification?.body || "",
    channel: msg.data?.channel || "PUSH",
    data: msg.data,
    metadata: msg.data || {},
  };
}

export default NotificationContainer;
